import React, { useState } from 'react';
import axios from 'axios';
import './GeneratorBase.css';

const GeneratorBase = ({ 
  title, 
  description, 
  fields, 
  apiEndpoint, 
  saveDraftEndpoint,
  enableLinkedIn = false,
  postingTimeSummary = '',
  buttonIcon
}) => {
  const [formData, setFormData] = useState({});
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleGenerate = async () => {
    // Validate required fields
    const requiredFields = fields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !formData[f.name]);
    
    if (missingFields.length > 0) {
      setError(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setLoading(true);
    setError('');
    setOutput('');

    try {
      const response = await axios.post(`/api/${apiEndpoint}/generate`, formData);
      if (response.data.success) {
        setOutput(response.data.output);
      } else {
        setError('Failed to generate content');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (output) {
      navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSaveDraft = async () => {
    if (!draftTitle.trim()) {
      setError('Please enter a title for the draft');
      return;
    }

    if (!output) {
      setError('No content to save');
      return;
    }

    try {
      await axios.post(`/api/${saveDraftEndpoint}/save-draft`, {
        title: draftTitle,
        inputs: formData,
        output
      });
      alert('Draft saved successfully!');
      setDraftTitle('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save draft');
    }
  };

  const handleLinkedInPost = async () => {
    if (!output) {
      setError('No content to post to LinkedIn');
      return;
    }

    try {
      // Try to copy content so user can paste quickly in LinkedIn
      await navigator.clipboard.writeText(output);
    } catch (e) {
      console.warn('Clipboard copy failed, continuing to open LinkedIn.', e);
    }

    // Open LinkedIn feed where the user can paste and post
    window.open('https://www.linkedin.com/feed/', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="generator-container">
      <div className="generator-header">
        <h2>{title}</h2>
        <p className="generator-description">{description}</p>
      </div>

      <div className="generator-content">
        <div className="input-section">
          <h3>Input Parameters</h3>
          <div className="form-fields">
            {fields.map(field => (
              <div key={field.name} className="form-field">
                <label>
                  {field.label}
                  {field.required && <span className="required">*</span>}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    rows={field.rows || 4}
                  />
                ) : field.type === 'select' ? (
                  <select
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                  >
                    <option value="">Select {field.label}</option>
                    {field.options.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type || 'text'}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                  />
                )}
              </div>
            ))}
          </div>
          <button 
            className="generate-button" 
            onClick={handleGenerate}
            disabled={loading}
          >
           
            <span>{loading ? 'Generating...' : 'Generate Content'}</span>
          </button>
        </div>

        <div className="output-section">
          <h3>Generated Content</h3>
          {error && <div className="error-message">{error}</div>}
          {output && (
            <>
              <div className="output-actions">
                <button 
                  className="copy-button" 
                  onClick={handleCopy}
                >
                  {copied ? '✓ Copied!' : 'Copy to Clipboard'}
                </button>
                <div className="save-draft-input">
                  <input
                    type="text"
                    placeholder="Draft title..."
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                  />
                  <button 
                    className="save-button" 
                    onClick={handleSaveDraft}
                  >
                    Save Draft
                  </button>
                </div>
                {enableLinkedIn && (
                  <button
                    className="linkedin-button"
                    onClick={handleLinkedInPost}
                  >
                    Post on LinkedIn
                  </button>
                )}
              </div>
              {enableLinkedIn && postingTimeSummary && (
                <div className="posting-time-hint">
                  Recommended posting time: {postingTimeSummary}
                </div>
              )}
              <div className="output-preview">
                <pre>{output}</pre>
              </div>
            </>
          )}
          {!output && !loading && (
            <div className="empty-output">
              Fill in the form and click "Generate Content" to see results here.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneratorBase;
